function isFiniteNumber(n) {
  return typeof n === "number" && Number.isFinite(n);
}
// Number(null), Number(""), Number(" ") and Number(false) are all 0, so a plain
// Number()+isFinite guard silently accepts a missing value as zero. That is how
// a profile with a null height produced a confident 87 cm chest (73 + 0.06*0 +
// 0.20*70) and printed "Based on null cm" to the customer. Reject the empty
// shapes explicitly, then accept anything that genuinely reduces to a number —
// numeric strings included, since chart JSON has carried those before.
function toFiniteNumber(v) {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
// One dimension of the estimator: base + perCm*height + perKg*weight.
// Every coefficient arrives as JSON from the remotely editable shop metafield,
// so none can be assumed present or numeric. Coercing through Number() means a
// string-typed coefficient (`"0.2"`) can never turn `+` into string
// concatenation, and returning null on anything non-finite means a malformed
// chart produces NO recommendation rather than a confident wrong one.
function estimatorDimension(base, perCm, heightCm, perKg, weightKg) {
  const b = toFiniteNumber(base), aH = toFiniteNumber(perCm), aW = toFiniteNumber(perKg);
  if (b === null || aH === null || aW === null) return null;
  const v = b + aH * heightCm + aW * weightKg;
  return isFiniteNumber(v) ? round2(v) : null;
}
// FAILS CLOSED. This used to `throw` on an unknown gender and to read
// `estimator[gender]` unguarded, which TypeErrors when body_estimator is
// missing or renamed in the metafield. Both throws landed at module top level
// (rehydrate() runs synchronously — the file is loaded as type="module", so
// document.readyState is already "interactive"), which aborted module
// evaluation and silently unbound bindExplainer, bindA11y and
// bindSizeOverrideTracking. The wizard still opened, so it looked healthy while
// size_overridden — the telemetry the estimator re-specification depends on —
// stopped firing entirely. Returning null instead routes into the same
// customer-facing fallback every other malformed-chart branch already uses.
function estimateBody(gender, heightCm, weightKg, estimator) {
  const e = estimator && estimator[gender];
  if (!e) return null;
  const h = toFiniteNumber(heightCm), w = toFiniteNumber(weightKg);
  if (h === null || w === null) return null;
  const chest = estimatorDimension(e.chest_base, e.chest_per_cm_height, h, e.chest_per_kg_weight, w);
  const waist = estimatorDimension(e.waist_base, e.waist_per_cm_height, h, e.waist_per_kg_weight, w);
  const hip = estimatorDimension(e.hip_base, e.hip_per_cm_height, h, e.hip_per_kg_weight, w);
  if (chest === null || waist === null || hip === null) return null;
  return { chest, waist, hip };
}
function round2(n) {
  return Math.round(n * 100) / 100;
}
function adjustForBodyType(body, gender, bodyType, adjustments) {
  const byGender = adjustments && adjustments[gender];
  const adj = byGender && byGender[bodyType];
  if (!adj) return body;
  return {
    chest: body.chest + (adj.chest || 0),
    waist: body.waist + (adj.waist || 0),
    hip: body.hip + (adj.hip || 0)
  };
}
function recommendSize(target, fitType, chartKey) {
  let best = null;
  let bestDist = Infinity;
  for (const [size, dims] of Object.entries(fitType.sizes)) {
    const garment = dims[chartKey];
    if (garment == null) continue;
    const d = Math.abs(garment - target);
    // See recommendSizeMulti: ties prefer the LARGER garment value explicitly,
    // not "whichever key the metafield happens to list last".
    if (d < bestDist || (d === bestDist && best !== null && garment > best.garment)) {
      best = { size, distance: round2(d), garment };
      bestDist = d;
    }
  }
  if (!best) return null;
  const allValues = Object.values(fitType.sizes).map((s) => s[chartKey]).filter((v) => v != null);
  const min = Math.min(...allValues), max = Math.max(...allValues);
  best.outOfRange = target < min || target > max;
  return best;
}
function recommendSizeMulti(targets, fitType, primaryChartKey) {
  let best = null;
  let bestScore = Infinity;
  for (const [size, dims] of Object.entries(fitType.sizes)) {
    let score = 0;
    let usedWeight = 0;
    let primaryDist = null;
    let primaryGarment = null;
    for (const t of targets) {
      const garment = dims[t.chartKey];
      if (garment == null) continue;
      const d = Math.abs(garment - t.target);
      score += d * t.weight;
      usedWeight += t.weight;
      if (t.chartKey === primaryChartKey) {
        primaryDist = d;
        primaryGarment = garment;
      }
    }
    if (usedWeight === 0) continue;
    const normalizedScore = score / usedWeight;
    // Ties resolve to the LARGER garment measurement — the documented "size up
    // for comfort" rule (docs/size-finder-algorithm.md:259). That rule used to
    // be an accident: `<=` plus Object.entries order meant the winner was
    // whichever size appeared LAST in the hand-edited metafield JSON. Reversing
    // the key order flipped 33 targets by a full size, and 81 bodies reachable
    // from the wizard's own sliders land on an exact tie. Comparing the garment
    // value makes the documented rule the one the code actually enforces.
    const tie = normalizedScore === bestScore && best !== null;
    const tieGoesLarger = tie && typeof primaryGarment === "number" && typeof best.garment === "number" && primaryGarment > best.garment;
    if (normalizedScore < bestScore || tieGoesLarger) {
      best = {
        size,
        // NOT `primaryDist != null ? primaryDist : normalizedScore`. Shopify's CDN
        // re-minifies theme assets and collapses that exact shape into `??`
        // (ES2020), which this file must never contain — see the note above
        // computeRecommendation. A typeof test cannot be rewritten that way.
        // primaryDist is only ever null or a number, so this is equivalent.
        distance: round2(typeof primaryDist === "number" ? primaryDist : normalizedScore),
        garment: primaryGarment,
        score: round2(normalizedScore)
      };
      bestScore = normalizedScore;
    }
  }
  if (!best) return null;
  const primaryValues = Object.values(fitType.sizes).map((s) => s[primaryChartKey]).filter((v) => v != null);
  const primary = targets.find((t) => t.chartKey === primaryChartKey);
  if (primaryValues.length && primary) {
    const min = Math.min(...primaryValues), max = Math.max(...primaryValues);
    best.outOfRange = primary.target < min || primary.target > max;
  } else {
    best.outOfRange = false;
  }
  return best;
}
const GARMENT_SCORE_DIMS = {
  blazer: [{ bodyKey: "chest", chartKey: "chest", weight: 1 }],
  hoodie: [{ bodyKey: "chest", chartKey: "chest", weight: 1 }],
  zip_hoodie: [{ bodyKey: "chest", chartKey: "chest", weight: 1 }],
  pants: [{ bodyKey: "waist", chartKey: "waist_cm", weight: 1 }],
  sweatpants: [{ bodyKey: "waist", chartKey: "waist_cm", weight: 1 }]
};
const LENGTH_SAFETY = {
  // Tops — typical height/length ratio sits around 2.3–2.5 for "reaches hipbone"
  blazer: { chartKey: "length", maxHeightOverLength: 2.55, lengthChartKey: "length" },
  hoodie: { chartKey: "length", maxHeightOverLength: 2.55, lengthChartKey: "length" },
  zip_hoodie: { chartKey: "length", maxHeightOverLength: 2.65, lengthChartKey: "length" },
  // Pants — height/length ratio around 1.55–1.65 ("hits ankle")
  pants: { chartKey: "length_cm", maxHeightOverLength: 1.7, lengthChartKey: "length_cm" },
  sweatpants: { chartKey: "length_cm", maxHeightOverLength: 1.75, lengthChartKey: "length_cm" }
};
// Confidence is a badge-only trust signal, ordered low < medium < high.
const CONFIDENCE_LADDER = ["low", "medium", "high"];
// A secondary signal may cost AT MOST one level. An unrecognised level floors to
// "low" rather than being returned unchanged, so that if a rung is ever added
// above "high" a FAILED check can never hand out a better badge than the body
// earned.
function downgradeConfidence(level) {
  const i = CONFIDENCE_LADDER.indexOf(level);
  return i > 0 ? CONFIDENCE_LADDER[i - 1] : "low";
}
function brandSanityCheck(body, gender, brandSizes, brandMap, threshold = 4) {
  const entries = Object.entries(brandSizes || {}).filter(([, v]) => v && v.length);
  if (!entries.length) return null;
  let bestDelta = Infinity, bestBrand = null, bestSize = null;
  for (const [brand, size] of entries) {
    const byBrand = brandMap && brandMap[brand];
    const byGender = byBrand && byBrand[gender];
    const expected = byGender && byGender[size];
    if (expected == null) continue;
    const d = Math.abs(expected - body.chest);
    if (d < bestDelta) {
      bestDelta = d;
      bestBrand = brand;
      bestSize = size;
    }
  }
  if (bestBrand == null) return null;
  return { brand: bestBrand, size: bestSize, delta: round2(bestDelta), passed: bestDelta <= threshold };
}
// Widest gap between adjacent sizes on a chart's primary dimension. Grading is
// not uniform across the catalogue (blazers step 4cm, pants 5cm, hoodies 6cm),
// so confidence has to be judged relative to the garment's own grading rather
// than against one hardcoded centimetre figure.
function gradingStep(sizes, chartKey) {
  const vals = Object.values(sizes || {}).map((s) => s[chartKey]).filter((v) => v != null).sort((a, b) => a - b);
  let step = 0;
  for (let i = 1; i < vals.length; i++) step = Math.max(step, vals[i] - vals[i - 1]);
  return step;
}
function computeRecommendation(profile, garmentTypeKey, fitCategoryKey, chart, easeOverrideCm) {
  const garmentTypes = chart && chart.garment_types;
  const garment = garmentTypes && garmentTypes[garmentTypeKey];
  const fitCategories = chart && chart.fit_categories;
  const category = fitCategories && fitCategories[fitCategoryKey];
  if (!garment || !category) return null;
  const primaryBodyKey = garment.bodyKey;
  const primaryChartKey = garment.chartKey;
  if (!primaryBodyKey || !primaryChartKey) return null;
  const hasChartKey = Object.values(garment.sizes || {}).some((s) => s[primaryChartKey] != null);
  if (!hasChartKey) return null;
  // ⚠️ ES2019 RULE — do not write `x != null ? x : y` anywhere in this file.
  // Shopify's CDN re-minifies theme assets with a modern target and rewrites that
  // exact shape into `x ?? y` (ES2020). This module is loaded as type="module", so
  // a single `??` makes the WHOLE file fail to parse on any browser without
  // nullish coalescing (roughly pre-2020 Chrome/WebView/Safari) and the Size Finder
  // dies silently — the button still renders, because it is server-side Liquid, and
  // does nothing. size-finder.js was the only theme asset hitting this; every other
  // asset is clean because none used the pattern. A `typeof` test cannot be
  // collapsed into `??`, which is why both sites below use one.
  // Note `easeOverrideCm != null ? Number(...) : ...` is SAFE — the consequent is a
  // call, not the tested value, so the minifier leaves it alone.
  const easeByGender = category.ease_cm && category.ease_cm[profile.gender];
  const ease = easeOverrideCm != null ? Number(easeOverrideCm) : (typeof easeByGender === "number" ? easeByGender : 0);
  const estimate = estimateBody(profile.gender, profile.height_cm, profile.weight_kg, chart.body_estimator);
  // A malformed estimator, an unknown gender or a non-numeric height/weight all
  // land here as null, and are treated exactly like every other unusable-chart
  // branch above: no recommendation, so the customer gets the wired-up "we
  // couldn't compute a size" fallback instead of a body derived from a coerced
  // null (which silently read as 0 cm and produced a confident wrong size).
  if (!estimate) return null;
  const adjusted = adjustForBodyType(estimate, profile.gender, profile.body_type, chart.body_type_adjustments);
  const brandCheck = brandSanityCheck(adjusted, profile.gender, profile.brand_sizes, chart.brand_size_map);
  const dimsConfig = GARMENT_SCORE_DIMS[garmentTypeKey] || [{ bodyKey: primaryBodyKey, chartKey: primaryChartKey, weight: 1 }];
  const targets = dimsConfig.filter((d) => adjusted[d.bodyKey] != null).map((d) => ({ chartKey: d.chartKey, target: adjusted[d.bodyKey] + ease, weight: d.weight }));
  const sizeMatch = recommendSizeMulti(targets, garment, primaryChartKey);
  if (!sizeMatch) return null;
  let lengthWarning = null;
  const ls = LENGTH_SAFETY[garmentTypeKey];
  if (ls && sizeMatch.size != null && profile.height_cm) {
    const sizeRow = garment.sizes && garment.sizes[sizeMatch.size];
    const garmentLength = sizeRow && sizeRow[ls.lengthChartKey];
    if (garmentLength != null) {
      const ratio = profile.height_cm / garmentLength;
      if (ratio > ls.maxHeightOverLength) {
        lengthWarning = "may run short for your height";
      }
    }
  }
  // Confidence is a TRUST signal for the badge only. It never affects which size
  // is picked — sizeMatch is already decided above and is not read from here.
  //
  // The old ladder made "high" require brandCheck.passed, i.e. unreachable for
  // any customer who skipped the optional brand step (most of them). Combined
  // with the badge order in applyRecommendation, that INVERTED the message: a
  // body landing 0.24cm from a chart value was shown the red "double-check the
  // chart" hedge, while a body 2cm off got the reassuring green one. It now
  // keys off how cleanly the body lands inside the chart, measured against the
  // garment's own grading, and the brand answer can only downgrade.
  const step = gradingStep(garment.sizes, primaryChartKey);
  // Within 40% of a grading step of a real chart value = a clean match. Past
  // that the body sits nearer the midpoint, which is genuinely "between sizes".
  const cleanMatch = step ? sizeMatch.distance <= step * 0.4 : sizeMatch.distance <= 2;
  let confidence;
  if (sizeMatch.outOfRange) confidence = "low";
  else if (lengthWarning) confidence = "medium";
  else if (cleanMatch) confidence = "high";
  else confidence = "medium";
  // The brand answer is a SECONDARY signal and costs AT MOST one level (2026-08-09).
  //
  // It used to force "low" outright. Measured over 2026-08-04..09, n=210 wizard
  // recommendations, that fired on 46.2% of customers whose body sat comfortably
  // INSIDE the chart — nearly half of everyone was told "we are not sure" for a
  // reason they could do nothing about, on a recommendation that was usually
  // fine. The reference tables cannot discriminate in this market: Zara's
  // women's table stops at XL = 96 cm while our customers' mean estimated chest
  // is 97.6 cm, so almost everyone maps to "Zara XL" and then passes or fails on
  // which side of 96 ± 4 they happen to land. The mean chest of the failures is
  // 97.6 against 96.8 for the passes, which is the giveaway — it is not
  // detecting a size disagreement, it is coin-flipping.
  //
  // It still cannot RAISE confidence, "low" is still the floor, and it still
  // never touches sizeMatch, which is decided above this line.
  const confidenceBase = confidence;
  if (brandCheck && !brandCheck.passed) confidence = downgradeConfidence(confidence);
  // FORENSIC TELEMETRY (added 2026-08-01). Every intermediate the size decision
  // passes through is exposed on the return so the event layer can log it. This
  // block is PURELY ADDITIVE — it reads values already computed above and does
  // not feed back into `sizeMatch`, which is decided before this point. Anything
  // derivable offline from (gender, height, weight, body_type) + chart is NOT
  // duplicated here; only what cannot be reconstructed later is.
  const primaryTarget = targets.filter((t) => t.chartKey === primaryChartKey)[0];
  const easeSource = easeOverrideCm != null ? "override" : (easeByGender != null ? "category" : "none");
  const bodyTypeAdj = {
    chest: round2(adjusted.chest - estimate.chest),
    waist: round2(adjusted.waist - estimate.waist),
    hip: round2(adjusted.hip - estimate.hip)
  };

  const fitLabel = `${category.label} ${garment.label}`;
  const parts = [
    `Based on ${profile.height_cm} cm, ${profile.weight_kg} kg${profile.body_type ? `, ${profile.body_type} build` : ""}.`,
    `${fitLabel} runs with ~${ease} cm ease.`
  ];
  if (brandCheck && brandCheck.passed) parts.push(`Your ${brandCheck.brand.toUpperCase()} ${brandCheck.size} matches our ${sizeMatch.size}.`);
  if (lengthWarning) parts.push(`Heads-up: this size ${lengthWarning}.`);
  return {
    size: sizeMatch.size,
    confidence,
    // What the BODY alone earned, before the brand answer was allowed to cost a
    // level. The badge needs it to tell "genuinely between sizes" apart from
    // "matched cleanly, brand answer disagreed" — two states that now share the
    // "medium" level but must not share a message.
    confidenceBase,
    distance: sizeMatch.distance,
    outOfRange: sizeMatch.outOfRange,
    brandCheck,
    estimate: adjusted,
    explainer: parts.join(" "),
    fitLabel,
    ease,
    lengthWarning,
    score: sizeMatch.score,
    // --- forensic intermediates (additive, never read back by the algorithm) ---
    estimateRaw: estimate,
    bodyTypeAdj,
    easeSource,
    target: primaryTarget ? round2(primaryTarget.target) : null,
    garmentValue: sizeMatch.garment != null ? sizeMatch.garment : null,
    gradingStep: step,
    scoredDims: targets.map((t) => t.chartKey),
    bodyKey: primaryBodyKey,
    chartKey: primaryChartKey
  };
}
const STORAGE_KEY = "seamlessSizeProfile";
const STORAGE_VERSION = 1;
const STEPS = ["gender", "brand", "height", "weight", "body_type"];
// This file is loaded as <script type="module" defer>, so by the time it runs
// document.readyState is already "interactive" and every onReady() callback is
// invoked SYNCHRONOUSLY, as a top-level module statement. A throw in any one of
// them therefore aborts module evaluation and every onReady() below it never
// registers — which is how a single bad chart could leave the wizard opening
// normally while bindExplainer, bindA11y and bindSizeOverrideTracking were all
// silently dead, taking size_overridden telemetry with them. Isolating each
// callback keeps one broken binder from taking out the rest. Reported rather
// than swallowed, so scripts/storefront-error-scan.mjs can still see it.
function onReady(fn) {
  if (typeof document === "undefined") return;
  const run = () => {
    try {
      fn();
    } catch (e) {
      if (typeof console !== "undefined" && console.error) console.error("[size-finder] init step failed:", e);
    }
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
  else run();
}
function readChart() {
  const node = document.getElementById("sf-chart-data");
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch {
    return null;
  }
}
let _i18n = null;
function i18n() {
  if (_i18n) return _i18n;
  const node = document.getElementById("sf-i18n");
  try {
    _i18n = node ? JSON.parse(node.textContent) : {};
  } catch {
    _i18n = {};
  }
  return _i18n;
}
function fillTpl(tpl, vars) {
  return String(tpl || "").replace(/\{(\w+)\}/g, (_, k) => vars[k] != null ? vars[k] : "");
}
// brand_size_map keys are storage slugs ("hm", "american_eagle"), never display
// text. Printing one raw would put "your AMERICAN_EAGLE size differs" on the
// badge, in both languages. The modal already renders proper labels next to each
// brand selector; this reads the same list out of the i18n blob so Arabic can
// localise it, and falls back to a readable Title Case rather than the slug.
// `labels` is injectable so the mapping is testable without a DOM; the badge
// calls it with one argument and it reads the i18n blob itself.
function brandLabel(key, labels) {
  const raw = String(key || "").trim();
  if (!raw) return "";
  const map = labels || i18n().brandLabels;
  const label = map && map[raw];
  if (label) return label;
  // Title Case beats the raw slug for a brand we forgot to label, but it cannot
  // recover "H&M" from "hm", which is why the map exists.
  return raw.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
function localizedExplainer(rec, profile) {
  const T = i18n();
  if (!T.explainer || (T.lang || "en") === "en") return rec.explainer;
  const ex = T.explainer;
  const build = profile.body_type ? fillTpl(ex.buildSuffix, { type: T.bodyTypes && T.bodyTypes[profile.body_type] || profile.body_type }) : "";
  const parts = [
    fillTpl(ex.based, { h: profile.height_cm, w: profile.weight_kg, build }),
    fillTpl(ex.ease, { fit: rec.fitLabel, ease: rec.ease != null ? rec.ease : "" })
  ];
  if (rec.brandCheck && rec.brandCheck.passed) {
    parts.push(fillTpl(ex.brandMatch, {
      brand: String(rec.brandCheck.brand || "").toUpperCase(),
      size: rec.brandCheck.size,
      our: rec.size
    }));
  }
  if (rec.lengthWarning) parts.push(ex.lengthWarn);
  return parts.join(" ");
}
function readProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return (p && p.version) === STORAGE_VERSION ? p : null;
  } catch {
    return null;
  }
}
function writeProfile(profile) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...profile, version: STORAGE_VERSION, saved_at: Date.now() }));
  } catch {
  }
}
function showStep(idx) {
  const modal = document.getElementById("sf-modal");
  if (!modal) return;
  modal.querySelectorAll("[data-sf-step]").forEach((el) => el.hidden = true);
  const stepName = idx === STEPS.length ? "result" : STEPS[idx];
  const target = modal.querySelector(`[data-sf-step="${idx + 1}"], [data-sf-step="${stepName}"]`);
  if (target) target.hidden = false;
  const pct = idx === STEPS.length ? 100 : Math.round((idx + 1) / STEPS.length * 100);
  modal.querySelector("[data-sf-progress]").style.width = pct + "%";
}
// height_cm/weight_kg carry DEFAULTS. A customer who clicks straight through the
// slider steps is silently scored as 170cm/65kg, and until 2026-08-01 nothing
// recorded whether a value was answered or merely defaulted — so a defaulted
// body was indistinguishable from a real one in every analysis. The *_touched
// flags below exist only to label that, and never affect the recommendation.
const state = { gender: null, brand_sizes: {}, height_cm: 170, weight_kg: 65, body_type: null, currentStep: 0, height_cm_touched: false, weight_kg_touched: false };
// "user" both answered · "partial" one · "default" neither · "unknown" a profile
// stored before the flags existed (old localStorage payloads have no flags).
// Only assert provenance we actually have. finish() used to write
// `!!state.height_cm_touched`, and `!!undefined` is false — which turned "we
// have no idea whether this body was answered" into "this body is positively
// the untouched 170/65 default", poisoning the one field added to let the
// estimator analysis exclude defaulted bodies. Omitting the key instead keeps
// profileSource() reporting "unknown" until the customer really moves a slider.
function profileProvenance(st) {
  const out = {};
  if (!st) return out;
  if (st.height_cm_touched !== undefined) out.height_cm_touched = !!st.height_cm_touched;
  if (st.weight_kg_touched !== undefined) out.weight_kg_touched = !!st.weight_kg_touched;
  return out;
}
function profileSource(profile) {
  if (!profile) return "unknown";
  const h = profile.height_cm_touched, w = profile.weight_kg_touched;
  if (h === undefined && w === undefined) return "unknown";
  if (h && w) return "user";
  if (h || w) return "partial";
  return "default";
}
// The shared body/decision block sent with BOTH recommendation_shown and
// size_overridden. Until 2026-08-01 neither event carried the body that produced
// the recommendation, so an override could not be joined to the customer who
// made it — which left the leading hypothesis (that the estimator under-predicts
// larger bodies) untestable on any amount of collected data. Everything derivable
// offline from gender+height+weight+body_type is deliberately NOT duplicated;
// this carries the inputs plus the values that would be unreconstructable later
// if the chart or the ease ever changes.
function decisionTelemetry(rec, profile) {
  const est = (rec && rec.estimate) || {};
  return {
    gender: profile && profile.gender,
    height_cm: profile && profile.height_cm,
    weight_kg: profile && profile.weight_kg,
    body_type: profile && profile.body_type,
    profile_source: profileSource(profile),
    est_chest: est.chest,
    est_waist: est.waist,
    // Added 2026-08-09 with the confidence softening. "medium" now pools two
    // different states — genuinely between sizes, and matched cleanly but the
    // brand answer disagreed — and confidence alone cannot separate them. This
    // rides all three events (recommendation_shown, size_overridden,
    // size_confirmed) because decisionTelemetry is spread into each.
    brand_check: rec && rec.brandCheck ? (rec.brandCheck.passed ? "pass" : "fail") : "absent",
    confidence_base: rec && rec.confidenceBase,
    ease_cm: rec && rec.ease,
    ease_source: rec && rec.easeSource,
    fit_target: rec && rec.target,
    match_distance: rec && rec.distance,
    out_of_range: rec ? String(!!rec.outOfRange) : undefined
  };
}
// Analytics bridge: keep the GTM-style dataLayer push (name with a dot) AND forward
// to the Google & YouTube app's gtag as an underscore event — GA4 event names
// reject dots, and bare dataLayer pushes have no consumer without a GTM container.
// send_to scopes it to GA4 only (not the AW-/MC- tags).
function sfTrack(name, params) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: "size_finder." + name }, params));
    if (typeof window.gtag === "function") {
      window.gtag("event", "size_finder_" + name, Object.assign({ send_to: "G-QRS2CB99T4" }, params));
    }
    // Clarity (project x4hqk32ywo): custom event = timeline marker + filterable;
    // custom tags = session-level filters for recordings/heatmaps.
    if (typeof window.clarity === "function") {
      window.clarity("event", "size_finder_" + name);
      window.clarity("set", "size_finder", name);
      if (name === "recommendation_shown" && params && params.recommended_size) {
        window.clarity("set", "sf_recommended_size", String(params.recommended_size));
      }
      if (name === "size_overridden" && params && params.chosen_size) {
        window.clarity("set", "sf_overrode_to", String(params.chosen_size));
      }
    }
  } catch (e) { /* analytics must never break the wizard */ }
}
// True while the wizard runs for the offer picker: its result then goes back
// to the picker's rows, never onto this page's own size picker.
let forOfferPicker = false;
function openModal(opts = {}) {
  const modal = document.getElementById("sf-modal");
  if (!modal) return;
  forOfferPicker = opts.entryPoint === "offer_picker";
  const existing = readProfile();
  if (existing) {
    Object.assign(state, existing);
    // Object.assign cannot restore flags a legacy payload never had, and `state`
    // is module-level, so without this the flags left over from an earlier
    // wizard run in the same page session stand in for the stored profile's
    // provenance. Assigning them unconditionally — undefined included — both
    // clears that leak and lets a pre-2026-08-01 profile keep reporting
    // "unknown" instead of being positively mislabelled as an untouched default.
    state.height_cm_touched = existing.height_cm_touched;
    state.weight_kg_touched = existing.weight_kg_touched;
  }
  state.currentStep = 0;
  // Repaint the sliders from the profile we just merged in, before the customer
  // can see or touch them.
  seedSliders();
  showStep(0);
  if (typeof modal.showModal === "function") modal.showModal();
  else modal.setAttribute("open", "");
  sfTrack("wizard_started", { entry_point: opts.entryPoint || "product" });
}
function closeModal() {
  const modal = document.getElementById("sf-modal");
  if (!modal) return;
  if (typeof modal.close === "function") modal.close();
  else modal.removeAttribute("open");
}
function next() {
  state.currentStep++;
  if (state.currentStep >= STEPS.length) return finish();
  showStep(state.currentStep);
}
function back() {
  if (state.currentStep === 0) return;
  state.currentStep--;
  showStep(state.currentStep);
}
function bindControls() {
  const modal = document.getElementById("sf-modal");
  if (!modal) return;
  document.querySelectorAll("[data-sf-open]").forEach((btn) => btn.addEventListener("click", () => openModal({ entryPoint: btn.dataset.sfGarmentType ? "product" : "page" })));

  // This module is queued behind swiper-bundle (134KB) in document order, so
  // the trigger is visible and tappable for a few hundred ms before the
  // listener above exists. The inline bootstrap in snippets/size-finder-button
  // records a tap made in that window; honour it rather than making the
  // customer tap a second time. Flag first, so the bootstrap stands down.
  window.sfReady = true;
  const pending = window.sfPendingOpen;
  if (pending) {
    window.sfPendingOpen = null;
    pending.removeAttribute("aria-busy");
    openModal({ entryPoint: pending.dataset.sfGarmentType ? "product" : "page" });
  }

  modal.querySelector("[data-sf-close]").addEventListener("click", closeModal);
  modal.querySelectorAll("[data-sf-next]").forEach((b) => b.addEventListener("click", next));
  modal.querySelectorAll("[data-sf-back]").forEach((b) => b.addEventListener("click", back));
  modal.addEventListener("click", (e) => {
    if (e.target === modal) closeModal();
  });
}
function bindStepInputs() {
  const modal = document.getElementById("sf-modal");
  if (!modal) return;
  modal.querySelectorAll("[data-sf-set='gender']").forEach((tile) => tile.addEventListener("click", () => {
    state.gender = tile.dataset.sfValue;
    modal.querySelectorAll("[data-sf-set='gender']").forEach((t) => t.classList.toggle("sf-selected", t === tile));
    populateBodyTypeGrid();
    setTimeout(next, 200);
  }));
  modal.querySelectorAll("[data-sf-brand]").forEach((sel) => sel.addEventListener("change", () => {
    state.brand_sizes[sel.dataset.sfBrand] = sel.value;
  }));
  modal.querySelectorAll("[data-sf-input]").forEach((inp) => {
    const key = inp.dataset.sfInput;
    const out = modal.querySelector(`[data-sf-output="${key}"]`);
    const U = i18n().units || { cm: "cm", kg: "kg" };
    const unit = key === "height_cm" ? U.cm : U.kg;
    inp.addEventListener("input", () => {
      state[key] = Number(inp.value);
      state[key + "_touched"] = true;
      if (out) out.textContent = `${inp.value} ${unit}`;
    });
  });
  seedSliders();
}
// Paint the sliders and their labels FROM `state`. This has to run again every
// time openModal merges a stored profile in, not just once at bind time: the
// bind happens at module evaluation, when state still holds the hardcoded
// 170/65 defaults, so a returning customer was shown 170/65 while the wizard
// computed from their real stored body. Because the input handler does
// `state[key] = Number(inp.value)`, the customer's first nudge of the slider
// did not adjust their stored value, it REPLACED it with one derived from the
// stale default — silently rewriting a stored 95 kg as 65 kg, which is more
// than a full grading step on both blazers.
function seedSliders() {
  const modal = document.getElementById("sf-modal");
  if (!modal) return;
  const U = i18n().units || { cm: "cm", kg: "kg" };
  modal.querySelectorAll("[data-sf-input]").forEach((inp) => {
    const key = inp.dataset.sfInput;
    const unit = key === "height_cm" ? U.cm : U.kg;
    const out = modal.querySelector(`[data-sf-output="${key}"]`);
    inp.value = state[key];
    if (out) out.textContent = `${state[key]} ${unit}`;
  });
}
function readBodyAssets() {
  const node = document.getElementById("sf-body-assets");
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch {
    return null;
  }
}
function populateBodyTypeGrid() {
  const grid = document.querySelector("[data-sf-bodytype-grid]");
  if (!grid || !state.gender) return;
  /* Women's "triangle" is gone from the picker on purpose: it was the same
     option offered twice. Its drawing was byte-identical to pear's, and
     body_type_adjustments.women.triangle held the same numbers as pear
     (chest -2, hip +4, waist -2) — in fashion terms they are synonyms. Two
     indistinguishable tiles out of seven made the choice ambiguous, and six
     shapes also fill a 3-column grid with no orphan.

     The `triangle` KEY is deliberately left in body_type_adjustments so a
     customer who already picked it keeps resolving to the same recommendation.
     Men's triangle is a genuinely distinct shape and stays. */
  const TYPES = state.gender === "women" ? ["pear", "rectangle", "hourglass", "inverted_triangle", "round", "diamond"] : ["rectangle", "triangle", "inverted_triangle", "oval", "trapezoid"];
  const labels = i18n().bodyTypes || { pear: "Pear", triangle: "Triangle", rectangle: "Rectangle", inverted_triangle: "Inverted Triangle", hourglass: "Hourglass", round: "Round", diamond: "Diamond", oval: "Oval", trapezoid: "Trapezoid" };
  const assets = readBodyAssets() || {};
  grid.innerHTML = TYPES.map((t) => {
    const byGender = assets[state.gender];
    const src = (byGender && byGender[t]) || "";
    return `
    <button type="button" class="sf-tile" data-sf-bodytype="${t}" aria-label="${(i18n().selectBodyType || "Select {label} body type").replace("{label}", labels[t])}">
      ${src ? `<img src="${src}" alt="${labels[t]}" width="80" height="147" decoding="async" onerror="this.style.visibility='hidden'">` : ""}
      <span class="sf-tile-label">${labels[t]}</span>
    </button>`;
  }).join("");
  grid.querySelectorAll("[data-sf-bodytype]").forEach((tile) => tile.addEventListener("click", () => {
    state.body_type = tile.dataset.sfBodytype;
    grid.querySelectorAll(".sf-tile").forEach((t) => t.classList.toggle("sf-selected", t === tile));
    setTimeout(finish, 250);
  }));
}
function readProductCtx() {
  const el = document.querySelector("[data-sf-garment-type]");
  if (!el) return null;
  return {
    garmentType: el.dataset.sfGarmentType,
    fitCategory: el.dataset.sfFitCategory,
    // Emitted by snippets/size-finder-button.liquid. Absent on a cached button
    // from before that change, which resolveSizeOptionName handles.
    sizeOption: el.dataset.sfSizeOption,
    easeOverride: el.dataset.sfEaseOverride !== undefined && el.dataset.sfEaseOverride !== "" ? Number(el.dataset.sfEaseOverride) : null
  };
}
function finish() {
  const chart = readChart();
  const ctx = readProductCtx() || { garmentType: "blazer", fitCategory: "slim", easeOverride: null };
  const profile = {
    gender: state.gender, height_cm: state.height_cm, weight_kg: state.weight_kg,
    body_type: state.body_type, brand_sizes: state.brand_sizes
  };
  Object.assign(profile, profileProvenance(state));
  writeProfile(profile);
  sfTrack("wizard_completed", {
    gender: profile.gender,
    body_type: profile.body_type,
    height_cm: profile.height_cm,
    weight_kg: profile.weight_kg,
    profile_source: profileSource(profile)
  });
  const rec = computeRecommendation(profile, ctx.garmentType, ctx.fitCategory, chart, ctx.easeOverride);
  showStep(STEPS.length);
  const fallback = i18n().result && i18n().result.fallback || "We couldn't compute a size \u2014 please pick manually.";
  document.querySelector("[data-sf-result-size]").textContent = rec ? rec.size : "\u2014";
  document.querySelector("[data-sf-result-explainer]").textContent = rec ? localizedExplainer(rec, profile) : fallback;
  setTimeout(() => {
    closeModal();
    if (forOfferPicker) {
      forOfferPicker = false;
      document.dispatchEvent(new CustomEvent("size-finder:profile-saved"));
      return;
    }
    if (rec) applyRecommendation(rec, ctx, profile, "wizard");
  }, 1500);
}

// The offer picker (assets/offer-picker.js, .scratch/offer-picker ticket 11)
// suggests a size for every piece of a set from the one stored profile, each
// product against its own chart, and runs this wizard once for all of them.
// A suggestion is a chart code (normalizeSizeValue), never a selection.
function suggestFor(garmentType, fitCategory, easeOverrideCm) {
  const profile = readProfile();
  const chart = readChart();
  if (!profile || !chart || !garmentType || !fitCategory) return null;
  const rec = computeRecommendation(profile, garmentType, fitCategory, chart, easeOverrideCm);
  return rec ? normalizeSizeValue(rec.size) : null;
}
if (typeof window !== "undefined") {
  window.SeamlessSizeFinder = {
    suggestFor,
    normalizeSizeValue,
    canOpen: () => !!document.getElementById("sf-modal"),
    open: () => openModal({ entryPoint: "offer_picker" })
  };
}
if (typeof window !== "undefined") {
  onReady(bindStepInputs);
}
function whenVariantReady(cb, tries = 30) {
  function isReady() {
    const el = document.querySelector("variant-radios, variant-selects");
    const defined = typeof window !== "undefined" && window.customElements && (customElements.get("variant-radios") || customElements.get("variant-selects"));
    return !!(el && defined);
  }
  function attempt(n) {
    if (isReady() || n <= 0) {
      requestAnimationFrame(() => requestAnimationFrame(cb));
      return;
    }
    requestAnimationFrame(() => attempt(n - 1));
  }
  // Start polling immediately rather than waiting for window.load (2026-08-09).
  //
  // The badge renders at DOMContentLoaded. Waiting for `load` left a window in
  // which the badge said one size while the picker still showed the server
  // default and Add to Cart was already clickable. Measured on the live PDP:
  // DCL 471ms, load 1189ms, so 718ms across 397 resources on a FAST connection,
  // and several seconds on Egyptian mobile. A customer who added during it got a
  // size_confirmed and an order stamp recording "size_changed / direction=down"
  // for a change she never made, contaminating the one field that exists to
  // settle sizing arguments.
  //
  // The `load` gate bought nothing. It was there to let <variant-radios> upgrade,
  // but product-variant.js is `defer`, so the element is already defined by
  // DOMContentLoaded (verified live), and isReady() polls for it regardless.
  //
  // Still rAF-driven on purpose: rAF is paused in a hidden tab, so a PDP opened
  // in a background tab correctly defers its auto-select until the customer
  // actually looks at it, then runs from the queued frame.
  attempt(tries);
}
function atcEnabled() {
  const submit = document.querySelector(".product-form__submit");
  return submit ? !submit.hasAttribute("disabled") : true;
}
// Was the recommended size actually buyable at this moment? Drives the
// voluntary-vs-forced override split: a sold-out recommendation FORCES a change
// (an inventory signal), while an in-stock one being rejected is a real fit
// signal (the only kind that should ever move ease_cm).
// "yes" | "no" | "unknown" — GA4 params must be scalars.
// The theme marks a sold-out option in TWO different ways, and they do not
// overlap in time:
//   1. `visually-disabled` — server-rendered by snippets/variant-color-swatch
//      .liquid at page load. This is the ONLY signal present when
//      recommendation_shown fires.
//   2. the `disabled` CLASS — added client-side by product-variant.js
//      setInputAvailability, which runs only after the customer changes some
//      other option (Color), i.e. strictly later than the recommendation.
// Checking `disabled` alone therefore reported every sold-out recommendation as
// "yes". That relabelled FORCED overrides (customer had no choice) as VOLUNTARY
// (a genuine fit signal) — corrupting the one input allowed to move ease_cm.
// Kept as a pure predicate so it is unit-testable without a DOM.
function isOptionUnavailable(el) {
  if (!el) return false;
  if (el.disabled) return true;
  const cl = el.classList;
  if (!cl || typeof cl.contains !== "function") return false;
  return cl.contains("visually-disabled") || cl.contains("disabled");
}
// A value read off the variant picker, mapped back to the chart's own size code.
// Shopify translates BOTH the option name and its values, so on /ar the picker
// offers صغير / وسط / كبير / XL and every `input[name="Size"]` selector in this
// file matched nothing: size_overridden had fired 122 times in English and 0
// times in Arabic against 28 Arabic recommendations, and autoSelectSize was a
// no-op there too. The vocabulary is copied from
// snippets/product-sticky-add-cart.liquid:395-406, which already had to solve
// this for the sticky size chips — one shared spelling, so the two cannot drift.
const SIZE_VALUE_MAP = {
  "small": "S", "صغير": "S",
  "medium": "M", "وسط": "M", "متوسط": "M",
  "large": "L", "كبير": "L",
  "x-large": "XL", "extra large": "XL", "كبير جدا": "XL", "كبير جدًا": "XL",
  "xx-large": "2XL"
};
function normalizeSizeValue(v) {
  if (v === null || v === undefined) return null;
  const raw = String(v).trim();
  if (!raw) return null;
  const mapped = SIZE_VALUE_MAP[raw.toLowerCase()];
  if (typeof mapped === "string") return mapped;
  // Already a chart code (S/M/L/XL/2XL), or a value we have no mapping for —
  // returned as-is rather than dropped, so an unknown vocabulary degrades to a
  // comparison that still works as long as both sides pass through here.
  return raw.toUpperCase();
}
// The size option's real name, resolved server-side by size-finder-button.liquid
// where Liquid can still see it. "Size" stays the fallback so an older cached
// button (no data-sf-size-option) keeps behaving exactly as before.
function resolveSizeOptionName(ctx) {
  const n = ctx && ctx.sizeOption;
  return typeof n === "string" && n ? n : "Size";
}
// The add-to-cart form THIS product's size picker belongs to.
//
// Scoping is required, not defensive. A PDP carries several add-to-cart forms:
// the buy box, the sticky bar, one per recommendation card, and — once the
// customer opens a quick view — a whole other product's <product-form>, which
// assets/quick_view_modal.js injects with `innerHTML` into #quickViewWrapper and
// LEAVES THERE after the modal closes. layout/theme.liquid renders that wrapper
// BEFORE <main id="MainContent">, so an unscoped document scan returns the
// quick-viewed product's radios FIRST. Measured on the live PDP:
// #quickViewWrapper at byte 141,772, #MainContent at 179,588, this product's own
// picker at 287,181. Unscoped, a customer who quick-viewed a Tank Top and then
// accepted the Blazer's recommendation would be recorded as having rejected it.
//
// The walk stops at <body> on purpose: body's first match in document order is
// exactly the quick-view form, which is the bug.
function pdpProductForm() {
  if (typeof document === "undefined") return null;
  const btn = document.querySelector("[data-sf-garment-type]");
  if (!btn) return null;
  let node = btn.parentElement;
  while (node && node.tagName !== "BODY") {
    if (typeof node.querySelector === "function") {
      const f = node.querySelector("form[data-type='add-to-cart-form']");
      if (f) return f;
    }
    node = node.parentElement;
  }
  return null;
}
function inQuickView(el) {
  if (!el || typeof el.closest !== "function") return false;
  return !!el.closest("#quickViewWrapper");
}
// Every <option>/<input> belonging to the size option, restricted to `scope`.
//
// Ownership, not containment: the variant picker sits OUTSIDE its <form> and is
// bound to it by the form="" attribute (verified live: the size radios carry
// form="product-form-<section>"), which the DOM exposes as el.form.
//
// Matching is done in JS rather than by interpolating the name into a CSS
// selector: option names are merchant-controlled and translated, and a quote or
// bracket in one would throw out of querySelector and take the whole binder down.
function sizeControls(optionName, scope) {
  const out = [];
  if (typeof document === "undefined") return out;
  const form = scope === undefined ? pdpProductForm() : scope;
  const name = String(optionName || "Size").toLowerCase();
  // If the form could not be resolved we still exclude the quick-view subtree,
  // so the known contaminant is gone even on an unexpected page structure. That
  // keeps auto-select working rather than silently disabling the feature.
  const keep = (el) => {
    if (inQuickView(el)) return false;
    if (!form) return true;
    return el.form === form;
  };
  const selects = document.querySelectorAll("select");
  for (let i = 0; i < selects.length; i++) {
    const sel = selects[i];
    if (String(sel.name || "").toLowerCase() !== "options[" + name + "]") continue;
    if (!keep(sel)) continue;
    for (let j = 0; j < sel.options.length; j++) out.push(sel.options[j]);
  }
  const radios = document.querySelectorAll("input[type='radio']");
  for (let i = 0; i < radios.length; i++) {
    const r = radios[i];
    if (String(r.name || "").toLowerCase() !== name) continue;
    if (!keep(r)) continue;
    out.push(r);
  }
  return out;
}
// Does this submit belong to THIS product's buy box?
//
// Pure and exported so the rule is unit-testable without a DOM. Without it,
// quick-adding a different product from the "You may also like" rail fires a
// size_confirmed carrying THIS product's id, body and recommendation — a
// fabricated fit signal in the one event that is meant to move ease_cm.
function isPdpAddToCartForm(form, ownForm) {
  if (!form || typeof form.getAttribute !== "function") return false;
  if (form.getAttribute("data-type") !== "add-to-cart-form") return false;
  if (ownForm && form === ownForm) return true;
  // The sticky buy bar is a SECOND form for the same product, rendered outside
  // the product section so it never equals ownForm. Only one exists per page.
  //
  // getAttribute("id"), NOT form.id. HTMLFormElement carries [LegacyOverrideBuiltIns]
  // in WebIDL, so a named control SHADOWS the built-in property — and both add-to-cart
  // forms contain <input name="id"> for the variant. Verified on the live PDP
  // 2026-08-09: form.id evaluates to "[object HTMLInputElement]", so this branch had
  // been dead since it was written and NO sticky-bar add has ever produced a
  // size_confirmed event. Every acceptance figure before that date is buy-box only.
  return String(form.getAttribute("id") || "").indexOf("product-sticky-form-") === 0;
}
function findSizeControl(size) {
  const want = normalizeSizeValue(size);
  if (!want) return null;
  const els = sizeControls(resolveSizeOptionName(readProductCtx()));
  for (let i = 0; i < els.length; i++) {
    if (normalizeSizeValue(els[i].value) === want) return els[i];
  }
  return null;
}
// The size the customer currently has selected, as a chart code.
function readSelectedSize() {
  const els = sizeControls(resolveSizeOptionName(readProductCtx()));
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    const chosen = el.tagName === "OPTION" ? el.selected : el.checked;
    if (chosen) return normalizeSizeValue(el.value);
  }
  return null;
}
function sizeInStock(size) {
  if (!size) return "unknown";
  const el = findSizeControl(size);
  if (!el) return "unknown";
  return isOptionUnavailable(el) ? "no" : "yes";
}
function autoSelectSize(recSize, attemptsLeft = 10) {
  const el = findSizeControl(recSize);
  if (!el) return;
  if (el.tagName === "OPTION") {
    const option = el;
    // Same two-signal problem as sizeInStock: auto-selecting a sold-out size
    // leaves Add to Cart disabled and spins the verify-retry below for nothing.
    if (isOptionUnavailable(option)) return;
    const select = option.closest("select");
    if (select && select.value !== option.value) {
      select.value = option.value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }
    return;
  }
  const radio = el;
  if (isOptionUnavailable(radio)) return;
  if (!radio.checked) {
    const label = document.querySelector(`label[for="${radio.id}"]`);
    if (label) label.click();
    else {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }
  } else if (attemptsLeft < 10) {
    radio.dispatchEvent(new Event("change", { bubbles: true }));
  }
  requestAnimationFrame(() => {
    if (atcEnabled() || attemptsLeft <= 0) return;
    autoSelectSize(recSize, attemptsLeft - 1);
  });
}
// Session-scoped one-shot latch, used to keep an event idempotent.
//
// sessionStorage rather than a module-level Set on purpose: the duplicates we
// are removing come from PAGE LOADS (rehydrate re-runs on every product page,
// and a refresh or back-nav re-runs it again), and a module Set resets on each
// of those — it would suppress nothing. Returns true if `key` was already seen.
//
// FAILS OPEN. Private browsing and blocked storage throw here, and in that case
// we would rather over-count than lose the event entirely: an inflated count is
// visible in the data, a silently dropped one is not.
const SEEN_KEY = "seamlessSizeSeen";
function seenOnce(key) {
  if (typeof sessionStorage === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    const seen = raw ? JSON.parse(raw) : {};
    if (seen[key]) return true;
    seen[key] = 1;
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    return false;
  } catch (e) {
    return false;
  }
}
function analyticsProductId() {
  const meta = typeof window !== "undefined" && window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
  const product = meta && meta.product;
  return product && product.id;
}
// The recommendation currently on the badge, plus how it got there. Read by the
// add-to-cart confirm so it reports the SAME decision the customer actually saw,
// rather than re-deriving one (bindSizeOverrideTracking re-parses the badge text
// with a regex; this is the structured version of that).
let lastRecommendation = null;
// Payload for size_confirmed. Pure and exported so it is unit-testable without a
// DOM — the binder below does the DOM work and hands the values in.
//
// This is the event the whole F1 change exists for: until now the only way to
// call a recommendation "accepted" was the ABSENCE of a size_overridden event,
// and recommendation_shown re-fires on every page load, so "accepted" mostly
// meant "never touched the size control". A confirm at add-to-cart is a positive
// signal instead of an inferred one.
function confirmPayload(rec, profile, ctx, finalSizeRaw, productId, recSource) {
  if (!rec || !rec.size) return null;
  const recommended = normalizeSizeValue(rec.size);
  const finalSize = normalizeSizeValue(finalSizeRaw);
  if (!recommended || !finalSize) return null;
  return Object.assign({
    recommended_size: recommended,
    final_size: finalSize,
    // GA4 params must be scalars, and the file already stringifies booleans this
    // way for out_of_range — same convention so both read alike in reports.
    size_changed: String(recommended !== finalSize),
    rec_source: recSource === "rehydrate" ? "rehydrate" : "wizard",
    garment_type: ctx && ctx.garmentType,
    fit_category: ctx && ctx.fitCategory,
    product_id: productId,
    confidence: rec && rec.confidence
  }, decisionTelemetry(rec, profile));
}
// Dedupe key for a confirm. Keyed on the OUTCOME, not on the submit: adding the
// same size twice tells us nothing new about fit, while switching size and
// adding again is genuinely new information and must be allowed through.
function confirmKey(productId, recommended, finalSize) {
  return "confirm|" + productId + "|" + recommended + "|" + finalSize;
}
// `profile` is optional and falls back to storage, so any existing 2-arg caller
// keeps working unchanged. `source` likewise: it labels which path produced this
// recommendation and defaults to "wizard" for any caller that omits it.
function applyRecommendation(rec, ctx, profile, source) {
  const wrap = document.querySelector("[data-sf-badge-wrap]");
  if (!wrap) return;
  wrap.hidden = false;
  const badge = wrap.querySelector("[data-sf-badge]");
  const txt = wrap.querySelector("[data-sf-badge-text]");
  badge.classList.remove("sf-medium");
  const R = i18n().result || {};
  // Driven by confidence, not by re-deriving distance. The old order tested
  // `distance >= 2` BEFORE confidence, so the closest matches fell past every
  // reassuring branch into the red hedge. Only a genuine problem is red now:
  // a size we do not make, or the customer's own brand size contradicting us.
  if (rec.outOfRange) {
    txt.textContent = fillTpl(R.outOfRange || "We don't carry your size yet \u2014 {size} is closest", { size: rec.size });
    badge.classList.add("sf-medium");
  } else if (rec.confidence === "high") {
    txt.textContent = fillTpl(R.recommended || "Recommended for you: {size}", { size: rec.size });
  } else if (rec.confidence === "medium") {
    // A body that matched cleanly and only lost a level to a disagreeing brand
    // answer is NOT between sizes, and must not be told that it is. Softening
    // the brand rule moves 36.6% of all customers into "medium" from a base of
    // "high" (measured 2026-08-04..09, n=213), so reusing the between-sizes copy
    // here would swap one false badge for another on a third of everyone.
    if (rec.confidenceBase === "high" && rec.brandCheck && !rec.brandCheck.passed) {
      txt.textContent = fillTpl(R.brandDiffers || "Recommended for you: {size} \xB7 your {brand} size differs", {
        size: rec.size,
        brand: brandLabel(rec.brandCheck.brand)
      });
    } else {
      const slimmer = rec.size === "M" || rec.size === "L";
      const tpl = slimmer ? R.betweenSlim || "Between sizes \xB7 {size} for a slimmer fit" : R.betweenComfort || "Between sizes \xB7 {size} for best comfort";
      txt.textContent = fillTpl(tpl, { size: rec.size });
    }
  } else {
    txt.textContent = fillTpl(R.probably || "Probably {size} for you \xB7 double-check the chart", { size: rec.size });
    badge.classList.add("sf-medium");
  }
  whenVariantReady(() => autoSelectSize(rec.size));
  const recSource = source === "rehydrate" ? "rehydrate" : "wizard";
  const productId = analyticsProductId();
  // Remember what the customer was actually shown, for the add-to-cart confirm.
  lastRecommendation = { rec: rec, ctx: ctx, profile: profile || readProfile(), source: recSource };
  // rehydrate() re-runs on EVERY product page load with a stored profile, which
  // is why recommendation_shown was firing 3.46x per completed wizard and why
  // "shown and not overridden" could never be used as an acceptance signal.
  // Dedupe the rehydrate path per product+size per session so a refresh or a
  // back-nav stops counting as a fresh impression. Wizard completions are NEVER
  // deduped: each one is a genuinely new answer from the customer.
  //
  // The event itself is deliberately NOT suppressed on rehydrate — it keeps
  // firing with rec_source="rehydrate" so every existing GA4 report, funnel and
  // audience built on recommendation_shown keeps working.
  if (recSource === "rehydrate" && seenOnce("shown|" + productId + "|" + rec.size)) return;
  sfTrack("recommendation_shown", Object.assign({
    product_id: productId,
    garment_type: ctx && ctx.garmentType,
    fit_category: ctx && ctx.fitCategory,
    recommended_size: rec.size,
    confidence: rec.confidence,
    recommended_in_stock: sizeInStock(rec.size),
    grading_step: rec.gradingStep,
    match_score: rec.score,
    garment_value: rec.garmentValue,
    rec_source: recSource
  }, decisionTelemetry(rec, profile || readProfile())));
}
function sizeAlias(s) {
  return { S: "Small", M: "Medium", L: "Large", XL: "XL", "2XL": "2XL" }[s] || s;
}
if (typeof window !== "undefined") {
  onReady(bindControls);
}
function rehydrate() {
  const profile = readProfile();
  if (!profile) return;
  const ctx = readProductCtx();
  if (!ctx || !ctx.garmentType || !ctx.fitCategory) return;
  const chart = readChart();
  const rec = computeRecommendation(profile, ctx.garmentType, ctx.fitCategory, chart, ctx.easeOverride);
  if (!rec) return;
  applyRecommendation(rec, ctx, profile, "rehydrate");
  const updateLabel = i18n().trigger && i18n().trigger.update || "Update my size profile";
  document.querySelectorAll("[data-sf-trigger-label]").forEach((el) => el.textContent = updateLabel);
}
if (typeof window !== "undefined") {
  onReady(rehydrate);
}
function bindExplainer() {
  document.querySelectorAll("[data-sf-explainer-link]").forEach((a) => a.addEventListener("click", (e) => {
    e.preventDefault();
    const profile = readProfile();
    if (!profile) return;
    const ctx = readProductCtx();
    if (!ctx) return;
    const rec = computeRecommendation(profile, ctx.garmentType, ctx.fitCategory, readChart(), ctx.easeOverride);
    if (!rec) return;
    showStep(STEPS.length);
    document.querySelector("[data-sf-result-size]").textContent = rec.size;
    document.querySelector("[data-sf-result-explainer]").textContent = localizedExplainer(rec, profile);
    const modal = document.getElementById("sf-modal");
    if (typeof modal.showModal === "function") modal.showModal();
    else modal.setAttribute("open", "");
  }));
}
if (typeof window !== "undefined") {
  onReady(bindExplainer);
}
function bindA11y() {
  const modal = document.getElementById("sf-modal");
  if (!modal) return;
  modal.addEventListener("close", () => {
    const trigger = document.querySelector("[data-sf-open]");
    if (trigger) trigger.focus();
  });
  const observer = new MutationObserver(() => {
    const visible = modal.querySelector("[data-sf-step]:not([hidden])");
    if (!visible) return;
    const focusable = visible.querySelector("button, select, input");
    if (focusable) focusable.focus();
  });
  observer.observe(modal, { attributes: true, attributeFilter: ["hidden"], subtree: true });
}
if (typeof window !== "undefined") {
  onReady(bindA11y);
}
function bindSizeOverrideTracking() {
  // Was `input[name='Size' i]`, which matches nothing on /ar (the group is
  // named المقاس there) — this binder registered zero listeners on the Arabic
  // storefront, so size_overridden stood at 122 English / 0 Arabic. Resolving
  // the real option name first is what makes the event exist at all on /ar.
  sizeControls(resolveSizeOptionName(readProductCtx())).forEach((inp) => {
    if (inp.tagName === "OPTION") return;
    inp.addEventListener("change", () => {
      const profile = readProfile();
      if (!profile) return;
      const wrap = document.querySelector("[data-sf-badge-wrap]");
      if (!wrap || wrap.hidden) return;
      const badgeText = wrap.querySelector("[data-sf-badge-text]");
      const badgeStr = (badgeText && badgeText.textContent) || "";
      const match = badgeStr.match(/\b(XS|S|M|L|XL|2XL)\b/);
      const recommended = match ? match[1] : undefined;
      // Compare through normalizeSizeValue rather than against the raw value and
      // its English alias, so "وسط" is recognised as M instead of reading as a
      // change away from every possible recommendation. `chosen_size` below still
      // reports the RAW value, unchanged, so existing reports keep their shape.
      const chosenCode = normalizeSizeValue(inp.value);
      if (recommended && chosenCode && normalizeSizeValue(recommended) !== chosenCode) {
        // recommended_in_stock decides voluntary (fit signal) vs forced (stock signal).
        // garment_type / fit_category / product_id come from the SAME sources as
        // recommendation_shown, so overrides can be split per garment before any
        // ease_cm is ever tuned — never tune the chart on a blended number.
        const octx = readProductCtx();
        const oMeta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
        const oProduct = oMeta && oMeta.product;
        // Recompute the recommendation from the SAME stored profile that produced
        // the badge, purely to attach the body and the intermediate values to the
        // override. It is deliberately not compared against `recommended` or used
        // to alter anything — the badge stays the source of truth for which size
        // was rejected, exactly as before.
        const orec = octx ? computeRecommendation(profile, octx.garmentType, octx.fitCategory, readChart(), octx.easeOverride) : null;
        sfTrack("size_overridden", Object.assign({
          recommended_size: recommended,
          chosen_size: inp.value,
          recommended_in_stock: sizeInStock(recommended),
          garment_type: octx && octx.garmentType,
          fit_category: octx && octx.fitCategory,
          product_id: oProduct && oProduct.id,
          confidence: orec && orec.confidence
        }, decisionTelemetry(orec, profile)));
      }
    });
  });
}
if (typeof window !== "undefined") {
  onReady(bindSizeOverrideTracking);
}
// --- Order stamping (2026-08-09) -------------------------------------------
//
// The recommendation has never survived the add-to-cart. Not one order in 609
// records what the wizard suggested, so every fit complaint that arrives weeks
// later is unjoinable to the size we told her to buy — which is why the same
// argument keeps being re-litigated on choosing-time proxies (overrides) that
// measure disagreement at the picker, not fit on a body. These properties put
// the decision on the ORDER, where a return, an exchange or a WhatsApp reply
// can be read against it.
//
// Keys are underscore-prefixed on purpose. Shopify hides those from the cart,
// the checkout and the order status page, and both cart templates in this theme
// already filter them (main-cart-items.liquid, cart-notification-product
// .liquid), so the customer never sees any of it. They stay readable in the
// admin and over the Admin API.
//
// DELIBERATELY ABSENT: height, weight, body type, estimated chest. GA4 already
// carries the body for calibration work. An order is a customer record with a
// name and a delivery address attached, and body measurements do not belong on
// it. A test pins that.
const SF_STAMP_VERSION = "1";
// The true size ladder for one garment, ordered by that garment's own chart
// measurements rather than by JSON key order — key order has produced a real
// bug in this file before — and derived per garment so a chart carrying 2XL
// ranks it above XL while a blazer chart correctly stops at XL.
function sizeLadder(chart, garmentTypeKey) {
  const garment = chart && chart.garment_types && chart.garment_types[garmentTypeKey];
  if (!garment || !garment.sizes) return [];
  const key = garment.chartKey;
  return Object.keys(garment.sizes)
    .filter((s) => garment.sizes[s] && garment.sizes[s][key] != null)
    .sort((a, b) => garment.sizes[a][key] - garment.sizes[b][key]);
}
// EVERY VALUE HERE MUST BE STABLE for a given (body, product, config). Shopify
// splits a cart line whenever any line-item property differs, underscore keys
// included, so an unstable field turns one item at quantity 2 into two rows of
// quantity 1 in the customer's cart. That is why rec_source is deliberately NOT
// stamped: it is "wizard" on the completion and "rehydrate" on every subsequent
// page load, so adding the same variant twice would visibly split her cart. It
// rides the size_confirmed GA4 event instead, which is where it is analysable.
//
// Pure and exported so the payload is unit-testable without a DOM.
function orderStamp(rec, ctx, finalSizeRaw, chart) {
  if (!rec || !rec.size || !ctx) return null;
  const recommended = normalizeSizeValue(rec.size);
  const finalSize = normalizeSizeValue(finalSizeRaw);
  if (!recommended || !finalSize) return null;
  const ladder = sizeLadder(chart, ctx.garmentType);
  const ri = ladder.indexOf(recommended);
  const fi = ladder.indexOf(finalSize);
  // null, not 0, when either size is off the ladder. A fabricated distance of 0
  // would read downstream as "she accepted it".
  const steps = ri >= 0 && fi >= 0 ? fi - ri : null;
  const changed = recommended !== finalSize;
  const out = {
    _sf_recommended_size: recommended,
    _sf_final_size: finalSize,
    _sf_size_changed: String(changed),
    _sf_direction: !changed ? "same" : steps === null ? "unknown" : steps > 0 ? "up" : steps < 0 ? "down" : "same",
    _sf_garment_type: ctx.garmentType,
    _sf_fit_category: ctx.fitCategory,
    _sf_confidence: rec.confidence,
    // What the body alone earned, before the brand answer cost a level. Without
    // it the new "medium" pools two different states — genuinely between sizes,
    // and matched-cleanly-but-brand-disagreed — with no way to separate them.
    _sf_confidence_base: rec.confidenceBase,
    // The ease and its source are what actually moved in F3, so together with the
    // chart version they identify the exact configuration that produced this
    // recommendation — without which a later re-read cannot tell an F3-sized
    // order from a pre-F3 one.
    _sf_ease_cm: rec.ease != null ? String(rec.ease) : null,
    _sf_ease_source: rec.easeSource,
    _sf_out_of_range: String(!!rec.outOfRange),
    _sf_chart_version: chart && chart.version != null ? String(chart.version) : null,
    _sf_stamp_version: SF_STAMP_VERSION
  };
  if (steps !== null) out._sf_steps = String(steps);
  return out;
}
// Written into the SUBMITTED form during the capture phase, which runs before
// <product-form>'s own submit handler builds its FormData from that same form,
// so the properties ride along on the cart/add request without touching
// product-form.js at all.
function stampAddToCartForm(form, props) {
  if (!form || typeof document === "undefined") return 0;
  // Clear our previous stamp FIRST, and before the props guard. A customer who
  // redoes the wizard and adds again would otherwise send both the stale and the
  // fresh value for every key; and if the stamp becomes uncomputable on a later
  // submit, the stale one must not silently ride along as if it were current.
  const old = form.querySelectorAll("input[data-sf-stamp]");
  for (let i = 0; i < old.length; i++) old[i].remove();
  if (!props) return 0;
  const keys = Object.keys(props);
  let written = 0;
  for (let i = 0; i < keys.length; i++) {
    const v = props[keys[i]];
    if (v === null || v === undefined || v === "") continue;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "properties[" + keys[i] + "]";
    input.value = String(v);
    input.setAttribute("data-sf-stamp", "");
    form.appendChild(input);
    written++;
  }
  return written;
}
// size_confirmed — the positive acceptance signal.
//
// DELEGATED ON document, CAPTURE PHASE. The theme renders TWO add-to-cart forms
// on a PDP (sections/main-product.liquid and snippets/product-sticky-add-cart
// .liquid), product-form.js calls preventDefault() and adds by fetch, and
// quick-view replaces its whole <product-form> subtree. Binding to the form
// nodes themselves would therefore either miss the sticky form or go stale;
// delegation survives every one of those. Capture so a stopPropagation()
// upstream cannot silently remove the signal.
//
// Fires on SUBMIT, not on fetch success: product-form.js exposes no success
// event, and the alternatives (monkey-patching its prototype, or wrapping
// window.fetch) are far more fragile than the small over-count that submitting a
// doomed add would cause. The add button is disabled for unavailable variants,
// so a submit is already a genuine attempt.
function bindAddToCartConfirm() {
  if (typeof document === "undefined") return;
  document.addEventListener("submit", (e) => {
    try {
      const form = e.target;
      // Must be THIS product's buy box. A PDP also carries add-to-cart forms for
      // every recommendation card and, once opened, for a quick-viewed product.
      if (!isPdpAddToCartForm(form, pdpProductForm())) return;
      const last = lastRecommendation;
      // No badge shown means no recommendation to confirm or contradict. This is
      // also what keeps a PDP that never opened the wizard silent.
      if (!last || !last.rec) return;
      const ctx = readProductCtx();
      if (!ctx || !ctx.garmentType) return;
      const finalSize = readSelectedSize();
      if (!finalSize) return;
      // Stamp the cart line FIRST, unconditionally, and before the analytics
      // guards below. The GA4 event is deduped on the outcome because a repeat
      // add tells us nothing new; the cart line is the opposite case. Skipping
      // the stamp on a second add would put an UNSTAMPED line into the cart for
      // the same variant, and Shopify keeps lines with different properties
      // separate — so the customer would see her item split in two, and half
      // the units would carry no recommendation.
      stampAddToCartForm(form, orderStamp(last.rec, ctx, finalSize, readChart()));
      const productId = analyticsProductId();
      const payload = confirmPayload(last.rec, last.profile || readProfile(), ctx, finalSize, productId, last.source);
      if (!payload) return;
      // Idempotency. Keyed on the OUTCOME (product + recommended + final), so
      // re-adding the same size, a double submit, or a re-render replaying the
      // event stays one confirmation, while switching size and adding again is
      // genuinely new information and is allowed through.
      if (seenOnce(confirmKey(productId, payload.recommended_size, payload.final_size))) return;
      sfTrack("size_confirmed", payload);
    } catch (err) {
      /* Analytics must never break add-to-cart. */
    }
  }, true);
}
if (typeof window !== "undefined") {
  onReady(bindAddToCartConfirm);
}
export {
  adjustForBodyType,
  brandLabel,
  brandSanityCheck,
  computeRecommendation,
  confirmKey,
  confirmPayload,
  decisionTelemetry,
  downgradeConfidence,
  estimateBody,
  isOptionUnavailable,
  orderStamp,
  sizeLadder,
  stampAddToCartForm,
  isPdpAddToCartForm,
  normalizeSizeValue,
  whenVariantReady,
  profileProvenance,
  profileSource,
  recommendSize,
  recommendSizeMulti,
  resolveSizeOptionName
};
